"use client"

import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Upload, Loader2, Check, X } from "@/lib/icons"
import { cn } from "@/lib/utils"
import type { CollectionListItem } from "./types"
import { useTranslations } from "next-intl"

const MAX_TAGS = 10

interface CollectionFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Present when editing an existing folio; absent when creating a new one. */
  collection?: CollectionListItem | null
  onSaved: () => void
  /** Creating a community folio (a public folio other people post into). Editing follows the folio's own kind. */
  kind?: "personal" | "community"
}

export function CollectionFormDialog({ open, onOpenChange, collection, onSaved, kind = "personal" }: CollectionFormDialogProps) {
  const tt = useTranslations("collectionFormDialog")
  const tc = useTranslations("communityFolios")
  const isCommunity = (collection?.kind ?? kind) === "community"
  const [rules, setRules] = useState("")
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [coverImageUrl, setCoverImageUrl] = useState("")
  const [isPublic, setIsPublic] = useState(true)
  const [tags, setTags] = useState<string[]>([])
  const [tagInput, setTagInput] = useState("")
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    setName(collection?.name ?? "")
    setDescription(collection?.description ?? "")
    setCoverImageUrl(collection?.cover_image_url ?? "")
    setIsPublic(collection?.is_public ?? true)
    setTags(collection?.tags ?? [])
    setRules(collection?.rules ?? "")
    setTagInput("")
    setError(null)
  }, [open, collection])

  function addTag(raw: string) {
    const tag = raw.trim().toLowerCase().slice(0, 30)
    if (!tag || tags.includes(tag) || tags.length >= MAX_TAGS) return
    setTags((t) => [...t, tag])
  }
  function handleTagInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      addTag(tagInput)
      setTagInput("")
    } else if (e.key === "Backspace" && !tagInput && tags.length > 0) {
      setTags((t) => t.slice(0, -1))
    }
  }

  async function handleFileSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file) return
    setUploading(true)
    setError(null)
    try {
      const formData = new FormData()
      formData.append("file", file)
      const res = await fetch("/api/upload", { method: "POST", body: formData })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error || "Upload failed")
      }
      const { url } = await res.json()
      setCoverImageUrl(url)
    } catch (err: any) {
      setError(err?.message ?? "Upload failed")
    } finally {
      setUploading(false)
    }
  }

  async function handleSave() {
    if (!name.trim()) return
    setSaving(true)
    setError(null)
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        coverImageUrl: coverImageUrl.trim() || null,
        isPublic: isCommunity ? true : isPublic,
        tags,
        ...(isCommunity ? { rules: rules.trim(), ...(collection ? {} : { kind: "community" }) } : {}),
      }
      const res = collection
        ? await fetch(`/api/collections/${collection.id}`, {
            method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
          })
        : await fetch("/api/collections", {
            method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
          })

      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body?.error || "Failed to save folio")
      }

      onOpenChange(false)
      onSaved()
    } catch (err: any) {
      setError(err?.message ?? "Failed to save folio")
    } finally {
      setSaving(false)
    }
  }

  // Only offered when editing a folio that already has items in it.
  const pickableThumbnails = collection?.previewThumbnails ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{collection ? tt("editFolio") : isCommunity ? tc("createCommunityFolio") : tt("createFolio")}</DialogTitle>
          <DialogDescription>
            {isCommunity ? tc("communityHint") : collection ? "Update this folio's details." : "Create a new folio to organize your favorite scripts and content."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="collection-name">{tt("name")}</Label>
            <Input id="collection-name" placeholder={tt("myFolio")} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="collection-desc">{tt("description")}</Label>
            <Textarea id="collection-desc" placeholder={tt("whatSThisFolioAbout")} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          <div className="space-y-2">
            <Label>{tt("coverImage")}</Label>

            {coverImageUrl && (
              <div className="relative w-full h-32 rounded-lg overflow-hidden border border-border">
                <img src={coverImageUrl} alt={tt("coverPreview")} className="w-full h-full object-cover" />
              </div>
            )}

            <div className="flex gap-2">
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelected} />
              <Button type="button" variant="outline" className="flex-1" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                {uploading ? tt("uploading") : tt("uploadImage")}
              </Button>
            </div>

            {pickableThumbnails.length > 0 && (
              <div className="space-y-1.5 pt-1">
                <p className="text-xs text-muted-foreground">{tt("orPickAThumbnailFromWhatSAlreadyInThis")}</p>
                <div className="flex flex-wrap gap-2">
                  {pickableThumbnails.map((url, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setCoverImageUrl(url)}
                      className={cn(
                        "relative w-14 h-14 rounded-md overflow-hidden border-2 transition-all",
                        coverImageUrl === url ? "border-primary" : "border-transparent hover:border-muted-foreground/40",
                      )}
                    >
                      <img src={url} alt="" className="w-full h-full object-cover" />
                      {coverImageUrl === url && (
                        <div className="absolute inset-0 bg-primary/30 flex items-center justify-center">
                          <Check className="h-4 w-4 text-white" />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <Input
              placeholder={tt("orPasteAnImageURL")}
              value={coverImageUrl}
              onChange={(e) => setCoverImageUrl(e.target.value)}
              className="text-xs"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="collection-tags">{tt("tags")}</Label>
            <p className="text-xs text-muted-foreground">{tt("helpsOthersFindThisFolioWhenSearchingExpl")}</p>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {tags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="gap-1 pr-1">
                    {tag}
                    <button
                      type="button"
                      onClick={() => setTags((t) => t.filter((x) => x !== tag))}
                      className="rounded-full hover:bg-muted-foreground/20"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            {tags.length < MAX_TAGS && (
              <Input
                id="collection-tags"
                placeholder={tt("typeATagAndPressEnter")}
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                onBlur={() => { if (tagInput) { addTag(tagInput); setTagInput("") } }}
              />
            )}
          </div>

          {isCommunity ? (
            <div className="space-y-2">
              <Label htmlFor="collection-rules">{tc("rules")}</Label>
              <Textarea id="collection-rules" placeholder={tc("rulesPlaceholder")} value={rules} onChange={(e) => setRules(e.target.value)} maxLength={1000} />
              <p className="text-xs text-muted-foreground">{tc("alwaysPublic")}</p>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="collection-public">{tt("public")}</Label>
                <p className="text-xs text-muted-foreground">{tt("othersCanViewAndRateThisFolio")}</p>
              </div>
              <Switch id="collection-public" checked={isPublic} onCheckedChange={setIsPublic} />
            </div>
          )}
          {isCommunity && !collection && (!description.trim() || tags.length === 0) && (
            <p className="text-xs text-muted-foreground">{!description.trim() ? tc("descriptionNeeded") : tc("tagNeeded")}</p>
          )}
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button
            className="w-full"
            onClick={handleSave}
            disabled={!name.trim() || saving || (isCommunity && !collection && (!description.trim() || tags.length === 0))}
          >
            {saving ? tt("saving") : collection ? tt("saveChanges") : isCommunity ? tc("createCommunityFolio") : tt("createFolio")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
